import { differenceInCalendarDays, format, parseISO } from 'date-fns'
import { tr } from 'date-fns/locale'
import { Activity, CalendarClock, CheckCircle2, ExternalLink, FileSignature, Gauge, Layers, Pencil, Plus, Receipt, Server, Trash2, Wallet, Wrench } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams, useSearchParams } from 'react-router'
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts'
import { toast } from 'sonner'
import { IconAction } from '@/components/common/IconAction'
import { PageHeader } from '@/components/common/PageHeader'
import { Pager } from '@/components/common/Pager'
import { EmptyState, ErrorState } from '@/components/common/States'
import { usePagination } from '@/components/common/usePagination'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { confirmModal } from '@/components/ui/modal'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Kpi, Section } from '@/features/finance/components/FinanceBits'
import { toastError } from '@/features/finance/ui'
import { AXIS_TICK, GRID_STROKE, legendOf, SERIES, TOOLTIP_STYLE } from '@/features/reports/visuals'
import { useAuth } from '@/lib/auth/AuthProvider'
import { fmtDate, fmtMoney, fmtRelative } from '../shared/format'
import { useDeleteServerPayment, useMarkServerPaymentPaid, useServerDetail, useSetMaintenance } from './api'
import { MetaList, UsageBar } from './components/AdminBits'
import { ServerContractFormModal, ServerPaymentFormModal } from './components/ServerForms'
import type { ServerPayment, ServerPaymentStatus, ServerStatus } from './types'

const TABS = ['general', 'payments', 'usage', 'placements'] as const
type Tab = (typeof TABS)[number]
const STATUS_TONE: Record<ServerStatus, BadgeTone> = { healthy: 'success', degraded: 'warning', maintenance: 'info', down: 'danger' }
const PAY_TONE: Record<ServerPaymentStatus, BadgeTone> = { paid: 'success', pending: 'warning', overdue: 'danger' }

export function InfraServerDetailPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const { id = '' } = useParams()
  const q = useServerDetail(id)
  const maintenance = useSetMaintenance()
  const [params, setParams] = useSearchParams()
  const tab: Tab = (TABS as readonly string[]).includes(params.get('tab') ?? '') ? (params.get('tab') as Tab) : 'general'
  const setTab = (v: Tab) =>
    setParams(
      (prev) => {
        const n = new URLSearchParams(prev)
        if (v === 'general') n.delete('tab')
        else n.set('tab', v)
        return n
      },
      { replace: true },
    )
  const [payForm, setPayForm] = useState<{ open: boolean; payment: ServerPayment | null }>({ open: false, payment: null })
  const [contractOpen, setContractOpen] = useState(false)
  const canWrite = can('infra.servers.write')
  const d = q.data
  const s = d?.server

  const toggleMaintenance = () => {
    if (!s) return
    const on = s.status !== 'maintenance'
    void confirmModal({
      title: on ? t('admin.infra.maintenanceOnTitle') : t('admin.infra.maintenanceOffTitle'),
      description: on ? t('admin.infra.maintenanceOnBody', { name: s.name, count: d.placements.filter((p) => p.status !== 'decommissioned').length }) : t('admin.infra.maintenanceOffBody', { name: s.name }),
      confirmLabel: on ? t('admin.infra.maintenanceOn') : t('admin.infra.maintenanceOff'),
    }).then((ok) => (ok ? maintenance.mutateAsync({ id: s.id, on }).then(() => toast.success(t('common.saved'))).catch(toastError) : undefined))
  }

  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />

  const renewDays = s ? differenceInCalendarDays(parseISO(s.nextRenewalAt), new Date()) : 0

  return (
    <div>
      <PageHeader
        backTo="/admin/infra"
        eyebrow={t('admin.infra.title')}
        title={s ? s.name : <Skeleton className="h-6 w-48" />}
        description={s ? `${s.hostname} · ${s.provider} · ${s.location}` : undefined}
        actions={
          s && canWrite ? (
            <>
              <Button variant="outline" onClick={toggleMaintenance} disabled={maintenance.isPending || s.role === 'backup'}>
                <Wrench />
                {s.status === 'maintenance' ? t('admin.infra.maintenanceOff') : t('admin.infra.maintenanceOn')}
              </Button>
              <Button onClick={() => setPayForm({ open: true, payment: null })}>
                <Plus />
                {t('admin.infra.payment.add')}
              </Button>
            </>
          ) : null
        }
      />

      {!d || !s ? (
        <div className="space-y-4">
          <Skeleton className="h-24 rounded-xl" />
          <Skeleton className="h-80 rounded-xl" />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Kpi icon={Wallet} tone="primary" label={t('admin.infra.detail.monthlyCost')} value={fmtMoney(s.monthlyCost)} footer={t(`admin.infra.billingCycle.${s.billingCycle}`)} />
            <Kpi icon={Receipt} tone="success" label={t('admin.infra.detail.totalPaid')} value={fmtMoney(d.totals.paid)} footer={t('admin.infra.detail.thisYear', { amount: fmtMoney(d.totals.thisYear) })} />
            <Kpi
              icon={CalendarClock}
              tone={Number(d.totals.pending) > 0 ? 'warning' : 'neutral'}
              label={t('admin.infra.detail.pending')}
              value={fmtMoney(d.totals.pending)}
              footer={renewDays < 0 ? t('admin.infra.detail.renewLate', { count: -renewDays }) : t('admin.infra.detail.renewIn', { count: renewDays, date: fmtDate(s.nextRenewalAt) })}
            />
            <Kpi icon={Activity} tone="info" label={t('admin.infra.detail.inUseFor')} value={t('admin.infra.detail.months', { count: d.totals.months })} footer={t('admin.infra.detail.since', { date: fmtDate(s.startedAt) })} />
          </div>

          <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
            <TabsList>
              <TabsTrigger value="general">
                <Server />
                {t('admin.infra.detail.tabs.general')}
              </TabsTrigger>
              <TabsTrigger value="payments">
                <Receipt />
                {t('admin.infra.detail.tabs.payments')}
              </TabsTrigger>
              <TabsTrigger value="usage">
                <Gauge />
                {t('admin.infra.detail.tabs.usage')}
              </TabsTrigger>
              <TabsTrigger value="placements">
                <Layers />
                {t('admin.infra.detail.tabs.placements', { count: d.placements.length })}
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {tab === 'general' ? (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              <Section title={t('admin.infra.detail.technical')} icon={Server}>
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <Badge tone={STATUS_TONE[s.status]}>{t(`admin.infra.status.${s.status}`)}</Badge>
                  <Badge tone="outline">{t(`admin.infra.roles.${s.role}`)}</Badge>
                </div>
                <MetaList
                  rows={[
                    [t('admin.infra.detail.hostname'), <span key="h" className="font-mono text-xs">{s.hostname}</span>],
                    ['IP', <span key="ip" className="font-mono">{s.ip}</span>],
                    [t('admin.infra.detail.os'), s.os],
                    [t('admin.infra.detail.resources'), `${s.vcpu} vCPU · ${s.ramGb} GB RAM · ${s.diskGb >= 1000 ? `${s.diskGb / 1000} TB` : `${s.diskGb} GB`}`],
                    [t('admin.infra.detail.uptime'), s.status === 'maintenance' ? t('admin.infra.inMaintenance') : t('admin.infra.uptime', { count: s.uptimeDays })],
                    [t('admin.infra.detail.lastBackup'), s.lastBackupAt ? fmtRelative(s.lastBackupAt) : t('admin.infra.noBackup')],
                  ]}
                />
                <div className="mt-4 grid grid-cols-3 gap-3">
                  <UsageBar value={s.cpu} label="CPU" />
                  <UsageBar value={s.ram} label="RAM" />
                  <UsageBar value={s.disk} label={t('admin.infra.disk')} />
                </div>
              </Section>
              <Section
                title={t('admin.infra.detail.contract')}
                icon={FileSignature}
                actions={
                  canWrite ? (
                    <Button size="sm" variant="outline" onClick={() => setContractOpen(true)}>
                      <Pencil />
                      {t('common.edit')}
                    </Button>
                  ) : null
                }
              >
                <MetaList
                  rows={[
                    [t('admin.infra.contract.provider'), s.provider],
                    [t('admin.infra.contract.plan'), s.plan],
                    [t('admin.infra.contract.contractNo'), <span key="c" className="font-mono">{s.contractNo || '—'}</span>],
                    [t('admin.infra.contract.startedAt'), fmtDate(s.startedAt)],
                    [t('admin.infra.contract.billingCycle'), t(`admin.infra.billingCycle.${s.billingCycle}`)],
                    [t('admin.infra.contract.monthlyCost'), fmtMoney(s.monthlyCost)],
                    [t('admin.infra.payment.method'), t(`admin.infra.paymentMethod.${s.paymentMethod}`)],
                    [t('admin.infra.contract.nextRenewalAt'), fmtDate(s.nextRenewalAt)],
                  ]}
                />
                {s.notes ? <p className="mt-3 rounded-lg bg-muted/40 p-3 text-sm">{s.notes}</p> : null}
              </Section>
            </div>
          ) : tab === 'payments' ? (
            <PaymentsTab payments={d.payments} canWrite={canWrite} onEdit={(p) => setPayForm({ open: true, payment: p })} />
          ) : tab === 'usage' ? (
            <Section title={t('admin.infra.detail.usageTitle')} icon={Gauge}>
              <ul className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                {legendOf(['CPU', 'RAM', t('admin.infra.disk')]).map((l) => (
                  <li key={l.label} className="inline-flex items-center gap-1.5">
                    <span className="size-2.5 rounded-sm" style={{ background: l.color }} aria-hidden />
                    {l.label}
                  </li>
                ))}
              </ul>
              <div className="h-72" role="img" aria-label={t('admin.infra.detail.usageTitle')}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={d.usage} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke={GRID_STROKE} strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={(v: string) => format(parseISO(v), 'd MMM', { locale: tr })} tick={AXIS_TICK} axisLine={false} tickLine={false} minTickGap={16} />
                    <YAxis domain={[0, 100]} tickFormatter={(v: number) => `%${v}`} tick={AXIS_TICK} axisLine={false} tickLine={false} width={44} />
                    <ChartTooltip contentStyle={TOOLTIP_STYLE} labelFormatter={(v) => fmtDate(String(v))} formatter={(v, n) => [`%${v}`, n]} cursor={{ stroke: 'var(--muted-foreground)', strokeDasharray: '3 3' }} />
                    {(['cpu', 'ram', 'disk'] as const).map((k, i) => (
                      <Line key={k} type="monotone" dataKey={k} name={k === 'disk' ? t('admin.infra.disk') : k.toUpperCase()} stroke={SERIES[i]} strokeWidth={2} dot={false} activeDot={{ r: 5, strokeWidth: 2, stroke: 'var(--card)' }} />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">{t('admin.infra.detail.usageHint')}</p>
            </Section>
          ) : (
            <Section title={t('admin.infra.detail.tabs.placements', { count: d.placements.length })} icon={Layers} bodyClassName="p-0">
              {d.placements.length ? (
                <Table>
                  <THead>
                    <TR className="hover:bg-transparent [&>th]:whitespace-nowrap">
                      <TH>{t('admin.infra.columns.url')}</TH>
                      <TH>{t('admin.infra.columns.customer')}</TH>
                      <TH>{t('admin.infra.columns.project')}</TH>
                      <TH>{t('admin.infra.columns.version')}</TH>
                      <TH>{t('admin.common.status')}</TH>
                      <TH className="w-px text-right">{t('admin.infra.columns.actions')}</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {d.placements.map((p) => (
                      <TR key={p.id}>
                        <TD>
                          <span className="font-mono text-sm">{p.url}</span>
                          <span className="block text-xs text-muted-foreground">{t(`admin.infra.envs.${p.env}`)}</span>
                        </TD>
                        <TD>{p.customerName}</TD>
                        <TD>{p.projectName}</TD>
                        <TD className="font-mono text-xs">{p.version}</TD>
                        <TD>
                          <Badge tone={p.status === 'running' ? 'success' : p.status === 'stopped' ? 'warning' : 'neutral'}>{t(`admin.infra.placementStatus.${p.status}`)}</Badge>
                        </TD>
                        <TD>
                          <div className="flex justify-end gap-1">
                            {p.customerId ? (
                              <Button size="icon" variant="outline" className="size-8" asChild>
                                <Link to={`/customers/${p.customerId}`} aria-label={t('admin.infra.detail.openCustomer', { name: p.customerName })}>
                                  <ExternalLink />
                                </Link>
                              </Button>
                            ) : null}
                          </div>
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              ) : (
                <EmptyState icon={Layers} title={t('admin.infra.detail.noPlacements')} />
              )}
            </Section>
          )}
        </div>
      )}

      {payForm.open && s ? <ServerPaymentFormModal key={payForm.payment?.id ?? 'new'} server={s} payment={payForm.payment} onDone={() => setPayForm({ open: false, payment: null })} /> : null}
      {contractOpen && s ? <ServerContractFormModal server={s} onDone={() => setContractOpen(false)} /> : null}
    </div>
  )
}

function PaymentsTab({ payments, canWrite, onEdit }: { payments: ServerPayment[]; canWrite: boolean; onEdit: (p: ServerPayment) => void }) {
  const { t } = useTranslation()
  const markPaid = useMarkServerPaymentPaid()
  const remove = useDeleteServerPayment()
  const { pageRows, pager } = usePagination(payments)

  const onDelete = async (p: ServerPayment) => {
    const ok = await confirmModal({ title: t('admin.infra.payment.deleteTitle'), description: t('admin.infra.payment.deleteBody', { invoice: p.invoiceNo || fmtDate(p.periodStart) }), confirmLabel: t('common.delete'), destructive: true })
    if (ok) remove.mutate(p.id, { onSuccess: () => toast.success(t('admin.infra.payment.deleted')), onError: toastError })
  }

  return (
    <Section title={t('admin.infra.detail.tabs.payments')} icon={Receipt} bodyClassName="p-0">
      {payments.length ? (
        <>
          <Table>
            <THead>
              <TR className="hover:bg-transparent [&>th]:whitespace-nowrap">
                <TH>{t('admin.infra.payment.period')}</TH>
                <TH>{t('admin.infra.payment.invoiceNo')}</TH>
                <TH className="text-right">{t('admin.infra.payment.amount')}</TH>
                <TH>{t('admin.infra.payment.method')}</TH>
                <TH>{t('admin.common.status')}</TH>
                <TH>{t('admin.infra.payment.paidAt')}</TH>
                {canWrite ? <TH className="w-px text-right">{t('admin.infra.columns.actions')}</TH> : null}
              </TR>
            </THead>
            <TBody>
              {pageRows.map((p) => (
                <TR key={p.id}>
                  <TD className="whitespace-nowrap text-sm">
                    {fmtDate(p.periodStart)} – {fmtDate(p.periodEnd)}
                    {p.note ? <span className="block text-xs text-muted-foreground">{p.note}</span> : null}
                  </TD>
                  <TD className="font-mono text-xs">{p.invoiceNo || '—'}</TD>
                  <TD className="text-right font-semibold tabular-nums">{fmtMoney(p.amount)}</TD>
                  <TD className="text-sm">{t(`admin.infra.paymentMethod.${p.method}`)}</TD>
                  <TD>
                    <Badge tone={PAY_TONE[p.status]}>{t(`admin.infra.payment.status.${p.status}`)}</Badge>
                  </TD>
                  <TD className="whitespace-nowrap text-sm tabular-nums">{p.date ? fmtDate(p.date) : '—'}</TD>
                  {canWrite ? (
                    <TD>
                      <div className="flex justify-end gap-1">
                        {p.status !== 'paid' ? <IconAction label={t('admin.infra.payment.markPaid')} icon={CheckCircle2} disabled={markPaid.isPending} onClick={() => markPaid.mutate(p.id, { onSuccess: () => toast.success(t('common.saved')), onError: toastError })} /> : null}
                        <IconAction label={t('common.edit')} icon={Pencil} onClick={() => onEdit(p)} />
                        <IconAction label={t('common.delete')} icon={Trash2} danger disabled={remove.isPending} onClick={() => void onDelete(p)} />
                      </div>
                    </TD>
                  ) : null}
                </TR>
              ))}
            </TBody>
          </Table>
          <Pager {...pager} />
        </>
      ) : (
        <EmptyState icon={Receipt} title={t('admin.infra.payment.empty')} />
      )}
    </Section>
  )
}
